"use client";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { tableApi } from "@/api/services";
import { whenApiKeyReady } from "@/api/http";
import { scopedStorageKey } from "@/lib/storageScope";

/**
 * Table context — captures the dine-in QR flow.
 *
 * On mount we parse `window.location.search` for `tableId`, `branchId`, and
 * `floorId` (mirroring v1's `TableContextResolver`). If `tableId` is present
 * we treat the session as a dine-in order until the tab/session ends.
 *
 * The resolved table info is mirrored into `sessionStorage` so an in-app
 * navigation that replaces the URL doesn't lose the dine-in signal.
 *
 * Once the tenant API key is ready, we validate the table against
 * `/v1/online-ordering/branches/{branchId}/tables/{tableId}` and enrich the
 * context with table number/status when available.
 */
export type TableInfo = {
  tableId: string;
  branchId: string | null;
  floorId: string | null;
  /** Human-readable table number/name if known. */
  tableName?: string | null;
  status?: string | null;
};

type TableCtx = {
  tableInfo: TableInfo | null;
  isDineIn: boolean;
};

const TableContext = createContext<TableCtx | null>(null);

const STORAGE_KEY = "tappya_table_v2";

function tableStorageKey(): string {
  return scopedStorageKey(STORAGE_KEY);
}

function readFromStorage(): TableInfo | null {
  try {
    const raw = sessionStorage.getItem(tableStorageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TableInfo;
    if (!parsed || typeof parsed.tableId !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeToStorage(info: TableInfo | null): void {
  try {
    const key = tableStorageKey();
    if (info) {
      sessionStorage.setItem(key, JSON.stringify(info));
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    /* storage unavailable */
  }
}

function readFromUrl(): TableInfo | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const tableId = params.get("tableId");
    if (!tableId) return null;
    return {
      tableId,
      branchId: params.get("branchId"),
      floorId: params.get("floorId"),
      tableName: params.get("tableNumber"),
      status: null,
    };
  } catch {
    return null;
  }
}

export function TableProvider({ children }: { children: ReactNode }) {
  const [tableInfo, setTableInfo] = useState<TableInfo | null>(() => {
    // URL takes priority — a fresh QR scan should override stale storage.
    return readFromUrl() ?? readFromStorage();
  });

  // Persist whenever it changes.
  useEffect(() => {
    writeToStorage(tableInfo);
  }, [tableInfo]);

  // Validate/enrich QR table context once the tenant API key is ready. This
  // mirrors v1's TableContextResolver and turns raw table IDs into a friendlier
  // table number/status when the endpoint is available.
  useEffect(() => {
    if (!tableInfo?.tableId || !tableInfo.branchId) return;
    if (tableInfo.tableName && tableInfo.status) return;
    let cancelled = false;

    (async () => {
      try {
        await whenApiKeyReady();
        if (cancelled) return;
        const details = await tableApi.getDetails(
          tableInfo.branchId as string,
          tableInfo.tableId,
        );
        if (cancelled) return;
        setTableInfo((current) => {
          if (
            !current ||
            current.tableId !== tableInfo.tableId ||
            current.branchId !== tableInfo.branchId
          ) {
            return current;
          }
          return {
            ...current,
            tableName:
              details.tableNumber ??
              details.tableName ??
              details.name ??
              current.tableName ??
              current.tableId,
            status: details.status ?? current.status ?? null,
            floorId: details.floorId ?? current.floorId,
          };
        });
      } catch (err) {
        console.warn("[table] validation failed", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tableInfo?.branchId, tableInfo?.status, tableInfo?.tableId, tableInfo?.tableName]);

  const value = useMemo<TableCtx>(
    () => ({
      tableInfo,
      isDineIn: !!tableInfo?.tableId,
    }),
    [tableInfo],
  );

  return <TableContext.Provider value={value}>{children}</TableContext.Provider>;
}

export function useTable(): TableCtx {
  const ctx = useContext(TableContext);
  if (!ctx) {
    throw new Error("useTable must be used inside <TableProvider>");
  }
  return ctx;
}
